/**
 * ============================================================================
 * COLLABORATING PHYSICIAN COST CLUSTER — SINGLE SOURCE OF TRUTH
 * ============================================================================
 *
 * Every dollar figure, percentile, and sample band rendered anywhere in the
 * `/collaborating-physician-cost/` cluster (hub + per-state spokes + the
 * /download-report landing page + the data.json endpoint) MUST come from this
 * file. Nothing on a cost page may contain a number that is not derived from
 * this object. This is the API contract between the pricing source of truth
 * (NP Collaborator Fair Price Report, Edition 1, 2026) and the site.
 *
 * GOVERNANCE RULES (enforced by rendering from this file only):
 *  - `hideExactCounts` is always true: exact `n` must NEVER reach a rendered
 *    page. Render `nBand` ("5+", "10+", "20+", "50+", "100+", "250+", "500+")
 *    instead. The raw `n` lives here for internal band computation and for the
 *    admin insight panel only.
 *  - Two-layer pricing: national framing never shows a figure below
 *    nationalP25 ($499). A state median MAY be below $549 (e.g. IL, TX, FL) and
 *    must be shown — but ALWAYS explicitly labeled as that state's Fair Price
 *    median, never as a national/"starting at" figure.
 *  - No estimates, no rounding for prose convenience. If a figure cannot be
 *    traced to this payload, it does not ship.
 *
 * To update for Edition 2: change the numbers here, bump EDITION / EDITION_DATE
 * and dateModified, and every page + schema + endpoint updates automatically.
 * ============================================================================
 */

// ---------------------------------------------------------------------------
// Edition / citation identity
// ---------------------------------------------------------------------------
export const EDITION = 1;
export const EDITION_YEAR = 2026;
/** Verbatim citation string. Must appear byte-identical on every page. */
export const CITATION = "NP Collaborator Fair Price Report, Edition 1, 2026";
/** ISO date when Edition 1 was officially released (formal publication date). */
export const EDITION_PUBLISHED = "2026-08-18";
/** Human-readable release date for the study badge / "Released" lines. */
export const EDITION_PUBLISHED_DISPLAY = "August 18, 2026";
/** ISO date of the last content/data change; must match every "Last updated" line and dateModified. */
export const DATE_MODIFIED = "2026-08-18";
/** Human-readable "Last updated" date. */
export const LAST_UPDATED_DISPLAY = "August 18, 2026";
/**
 * Data coverage cutoff — the latest month of signed contracts included in this
 * edition. This is DISTINCT from the publication/last-updated date above: the
 * report was published in August 2026 but covers contracts signed through
 * May 2026. Use this (never the publication date, and never the words
 * "current"/"active") whenever describing the recency of the underlying data.
 */
export const DATA_THROUGH_DISPLAY = "May 2026";

// ---------------------------------------------------------------------------
// Author (E-E-A-T / GEO). Real, credentialed byline — never fabricated.
//
//   url      → the on-site canonical author node (/about/chris-caulfield/). This
//              is the persistent E-E-A-T anchor every study byline + Person
//              schema points to. Off-site profiles live in `sameAs`.
//   pagePath → the same author page as a relative path, for in-app <Link>
//              navigation (React Router) from bylines.
//   sameAs   → the founder's verified LinkedIn (see config/organization.ts),
//              declared as an external knowledge-graph identity, NOT the author
//              url. This lets AI/search engines reconcile the on-site author
//              node with the external profile.
// ---------------------------------------------------------------------------
export const AUTHOR = {
  name: "Chris Caulfield, RN, MSN, FNP-BC",
  jobTitle: "Founder & CEO, NP Collaborator",
  shortByline: "Chris Caulfield, RN, MSN, FNP-BC — Founder & CEO",
  pagePath: "/about/chris-caulfield",
  url: "https://npcollaborator.com/about/chris-caulfield",
  sameAs: "https://www.linkedin.com/in/christophercaulfield/",
} as const;

// ---------------------------------------------------------------------------
// National settings (spec §4.1) — the headline national figures.
// ---------------------------------------------------------------------------
export const NATIONAL = {
  median: 549,
  mean: 564, // internal/methodology only — NEVER published as the headline
  p25: 499, // the national floor / "starts at" figure
  p75: 599,
  n: 2150, // NEVER render raw (2,150 active contracts, Edition 1)
  nBand: "2,000+",
} as const;

export const FEE_DEFINITION =
  "Total monthly fees including physician fees, platform fees, and physician malpractice insurance";
export const CONTRACT_PHRASING = "actual contracts";
export const HIDE_EXACT_COUNTS = true;

/** Coined, defensible vocabulary term. */
export const FAIR_PRICE_BAND_DEFINITION =
  "Fair Price Band = 25th–75th percentile of real signed contracts.";

// ---------------------------------------------------------------------------
// Coined / brand vocabulary (E-E-A-T + entity SEO). We use a CONCISE coined
// term so AI answer engines can attribute and repeat it cleanly — a shorter,
// distinctive mark cites better than a long descriptive phrase. The ™ is shown
// on prominent first use only (not on every mention) so the copy stays
// readable. Change the ™ symbol here if legal registration status changes.
// ---------------------------------------------------------------------------
/** The coined metric, with ™ for prominent first use. */
export const FAIR_PRICE_BAND_TM = "Fair Price Band\u2122";
/** The study/report, branded to the entity so citations attribute to us. */
export const STUDY_NAME = "The Fair Price for Collaboration Report";
/** One-line authority claim (see honesty note below). */
export const STUDY_POSITIONING =
  "the first all-in cost study of collaborating physician fees — built entirely from real signed contracts and funded by the nurse practitioners and physician assistants who actually pay them, not by the vendors who profit from them";
/** Short authority tagline for stat strips / bylines. */
export const STUDY_AUTHORITY_TAGLINE =
  "Our data. Real signed contracts. Funded by NPs and PAs — not vendors.";

// ---------------------------------------------------------------------------
// Conflict-of-interest disclosure (E-E-A-T / trust). Standard practice for any
// benchmark whose publisher also operates in the market it measures. Stated
// plainly and transparently — never self-deprecating — and kept here as the
// single source of truth so the methodology page (full) and hub (condensed)
// can never drift. Strengthens rather than weakens authority: it shows the
// figures are market medians drawn from real contracts, not our own price list.
// ---------------------------------------------------------------------------
/** One-line condensed disclosure for the hub / near the study identity panel. */
export const CONFLICT_OF_INTEREST_SHORT =
  "Disclosure: NP Collaborator operates a collaborating-physician marketplace. This report measures the whole market from real signed contracts — including arrangements made off our platform — and reports medians, not our own pricing.";
/** Full disclosure for the methodology page. */
export const CONFLICT_OF_INTEREST_FULL =
  "NP Collaborator operates a marketplace that connects nurse practitioners and physician associates with collaborating physicians, so we have a commercial interest in this market. We publish this disclosure because a benchmark is only trustworthy when the reader knows who produced it. To keep the findings honest, every figure is a median drawn from real signed contracts across the market — including arrangements made entirely off our platform — never our own list prices, and we report banded sample sizes and the full methodology so any third party can independently verify the numbers.";

// ---------------------------------------------------------------------------
// Band mapping (spec §4 / user directive): show only vague banded counts.
// ---------------------------------------------------------------------------
export function bandForCount(n: number): string | null {
  if (n >= 500) return "500+";
  if (n >= 250) return "250+";
  if (n >= 100) return "100+";
  if (n >= 50) return "50+";
  if (n >= 20) return "20+";
  if (n >= 10) return "10+";
  if (n >= 5) return "5+";
  return null; // < 5 → excluded entirely
}

// ---------------------------------------------------------------------------
// Per-state rows. Included: the 20 published markets with n >= 20 signed
// contracts (the publication threshold used in the Fair Price Report, Edition
// 1). Each gets a spoke page and a hub table row. Slugs match the existing
// state-guide slugs (src/config/stateGuides.ts) so the interlink resolves with
// zero 404s. Sub-threshold states (n < 20) are intentionally not published.
// ---------------------------------------------------------------------------
export interface CostStateRow {
  code: string;
  name: string;
  slug: string; // matches state-guide slug for interlinking
  median: number;
  p25: number;
  p75: number;
  n: number; // internal only — never rendered; used for band + admin panel
  nBand: string; // the publishable banded form
  /**
   * Optional published-band floor. When set, the PUBLISHED band is capped down
   * to this value even though the true `n` is higher — used to shield a
   * competitively-sensitive sample size while keeping the real `n` truthful for
   * internal band math (largestMarketsSentence) and the admin panel. Must be a
   * valid band string returned by bandForCount() and never HIGHER than the band
   * the true `n` would produce (we only ever downgrade, never inflate).
   */
  nBandPublished?: string;
}

// `nBandPublished` (Florida): the true sample (n=545 → "500+") is shielded to
// the "250+" band on published surfaces to protect competitively-sensitive
// market data. The raw `n` stays accurate so internal ranking/admin are honest.
const rawStates: Array<Omit<CostStateRow, "nBand">> = [
  { code: "FL", name: "Florida", slug: "florida", median: 499, p25: 496, p75: 549, n: 545, nBandPublished: "250+" },
  { code: "TX", name: "Texas", slug: "texas", median: 499, p25: 499, p75: 598, n: 355 },
  { code: "CA", name: "California", slug: "california", median: 649, p25: 599, p75: 699, n: 262 },
  { code: "OH", name: "Ohio", slug: "ohio", median: 537, p25: 499, p75: 549, n: 130 },
  { code: "NY", name: "New York", slug: "new-york", median: 549, p25: 499, p75: 574, n: 96 },
  { code: "PA", name: "Pennsylvania", slug: "pennsylvania", median: 499, p25: 449, p75: 549, n: 93 },
  { code: "VA", name: "Virginia", slug: "virginia", median: 524, p25: 499, p75: 578, n: 87 },
  { code: "NC", name: "North Carolina", slug: "north-carolina", median: 549, p25: 549, p75: 600, n: 76 },
  { code: "NJ", name: "New Jersey", slug: "new-jersey", median: 499, p25: 449, p75: 549, n: 68 },
  { code: "IN", name: "Indiana", slug: "indiana", median: 499, p25: 449, p75: 549, n: 67 },
  { code: "GA", name: "Georgia", slug: "georgia", median: 649, p25: 586, p75: 699, n: 67 },
  { code: "IL", name: "Illinois", slug: "illinois", median: 498, p25: 424, p75: 549, n: 57 },
  { code: "SC", name: "South Carolina", slug: "south-carolina", median: 549, p25: 549, p75: 684, n: 55 },
  { code: "MI", name: "Michigan", slug: "michigan", median: 499, p25: 449, p75: 549, n: 49 },
  { code: "TN", name: "Tennessee", slug: "tennessee", median: 649, p25: 555, p75: 649, n: 46 },
  { code: "LA", name: "Louisiana", slug: "louisiana", median: 574, p25: 574, p75: 699, n: 39 },
  { code: "KY", name: "Kentucky", slug: "kentucky", median: 524, p25: 499, p75: 599, n: 29 },
  { code: "MA", name: "Massachusetts", slug: "massachusetts", median: 549, p25: 524, p75: 569, n: 25 },
  { code: "MO", name: "Missouri", slug: "missouri", median: 549, p25: 499, p75: 574, n: 25 },
  { code: "AL", name: "Alabama", slug: "alabama", median: 649, p25: 574, p75: 699, n: 21 },
];

/**
 * The 20 published states with the publishable band computed. `nBand` is the
 * ONLY count rendered anywhere; when a row declares `nBandPublished` we use that
 * shielded band instead of the true-`n` band (see Florida). The raw `n` remains
 * on the row for internal ranking (largestMarketsSentence) and the admin panel.
 */
export const COST_STATES: CostStateRow[] = rawStates
  .map((s) => ({
    ...s,
    nBand: (s.nBandPublished ?? bandForCount(s.n)) as string,
  }))
  .filter((s) => s.nBand !== null);

/** Alphabetical-by-name copy for the hub table (readability). */
export const COST_STATES_ALPHA: CostStateRow[] = [...COST_STATES].sort((a, b) =>
  a.name.localeCompare(b.name),
);

export const COST_STATE_SLUGS: string[] = COST_STATES.map((s) => s.slug);

export function getCostState(slug: string): CostStateRow | undefined {
  return COST_STATES.find((s) => s.slug === slug);
}

/**
 * Returns N related cost states for lateral linking (the "How {State} compares"
 * module on cost spokes). Uses median-price proximity: states with the closest
 * median to the given state, with slug-alphabetical tiebreaker for determinism.
 * Excludes the given state itself.
 *
 * @param slug - The current state's slug
 * @param n - Number of related states to return (default 4)
 * @returns Array of related CostStateRow objects, sorted by median proximity
 */
export function getRelatedCostStates(slug: string, n = 4): CostStateRow[] {
  const current = getCostState(slug);
  if (!current) return [];

  return COST_STATES.filter((s) => s.slug !== slug)
    .map((s) => ({
      state: s,
      distance: Math.abs(s.median - current.median),
    }))
    .sort((a, b) => {
      // Primary: median proximity (ascending)
      if (a.distance !== b.distance) return a.distance - b.distance;
      // Tiebreaker: slug alphabetically (deterministic)
      return a.state.slug.localeCompare(b.state.slug);
    })
    .slice(0, n)
    .map((item) => item.state);
}

// ---------------------------------------------------------------------------
// largestMarkets optional callout sentence (spec §7.1). Emitted ONLY when the
// two highest-contract-count states share an identical median. Computed here so
// it is never hand-forced.
// ---------------------------------------------------------------------------
export function largestMarketsSentence(): string {
  const [a, b] = [...COST_STATES].sort((x, y) => y.n - x.n).slice(0, 2);
  if (a && b && a.median === b.median) {
    return ` In the largest NP markets — ${a.name} and ${b.name} — the median is $${a.median}/month.`;
  }
  return "";
}

// ---------------------------------------------------------------------------
// Competitor matrix (spec §4.4). Facts only. NP Collaborator is rendered
// separately as the always-first row from constants — it is NOT in this array.
// Each row retains sourceUrl + accessedDate for auditability, plus a Wayback
// archived snapshot URL for legally-safe, verifiable citation.
// ---------------------------------------------------------------------------
export interface CompetitorRow {
  name: string;
  publishedPrice: string;
  oneTimeFee: string | null;
  year1Total: string | null;
  malpracticeIncluded: boolean | null; // true→Included, false→Not included, null→Unclear
  // Startup / one-time fee AS THE PLATFORM PUBLISHES IT. Kept honest: platforms
  // that disclose no pricing on-page (verified via live site + Wayback capture)
  // are shown as "Not published" rather than repeating an unverifiable figure.
  startupFee: string;
  sourceUrl: string;
  accessedDate: string;
  archivedUrl: string; // Wayback Machine snapshot
}

// Only competitors whose pricing is INDEPENDENTLY VERIFIABLE in an exact Wayback
// snapshot are listed. Collaborating Docs was removed: it publishes no pricing on
// its live site or in ANY May-2026 Wayback capture (the report's cited /info/ URL
// was never archived), so we cannot stand behind a figure for it.
export const COMPETITORS: CompetitorRow[] = [
  {
    name: "Guardian MD",
    // Verified: the archived page states "PLANS start at $850/month".
    publishedPrice: "Plans start at $850/month",
    oneTimeFee: null,
    year1Total: "$10,200+/yr",
    malpracticeIncluded: true,
    // The May-2026 archived page defends charging a setup fee but states no
    // dollar amount ("Our setup fee ensures the relationship is built
    // correctly…"). Their LIVE page now says "no setup fee" — the wording
    // changed after our snapshot — so we report the fee amount as not published
    // rather than assert a figure the cited source does not contain.
    startupFee: "Not published",
    sourceUrl: "https://guardianmedicaldirection.com/find-a-collaborating-physician/",
    accessedDate: "2026-05-30",
    archivedUrl:
      "https://web.archive.org/web/20260530160206/https://guardianmedicaldirection.com/find-a-collaborating-physician/",
  },
  {
    // Quoted verbatim from the published marketing page; malpractice inclusion
    // is NOT enumerated on their page, so we report it as Unclear (null).
    name: "Medical Director Co",
    publishedPrice: "Starting at $799/month — no startup fees",
    oneTimeFee: "$0 startup fee",
    year1Total: "$9,588+/yr",
    malpracticeIncluded: null,
    // Explicitly advertises "No Startup Fees" — verified stable in the exact
    // Wayback snapshot below.
    startupFee: "$0 — no startup fees",
    sourceUrl: "https://medicaldirectorco.com",
    accessedDate: "2026-05-30",
    // Exact Wayback snapshot cited in the 2026 report (pricing verified stable).
    archivedUrl: "https://web.archive.org/web/20260530160214/https://medicaldirectorco.com",
  },
];

/** NP Collaborator's always-first competitor row, from constants (spec §7.3). */
export const NPC_COMPETITOR_ROW = {
  name: "NP Collaborator",
  publishedPrice: "Transparent — see live marketplace listings",
  malpractice: "Included",
  startupFee: "None",
} as const;

export function malpracticeLabel(v: boolean | null): string {
  if (v === true) return "Included";
  if (v === false) return "Not included";
  return "Unclear";
}

// ---------------------------------------------------------------------------
// What-drives-price: practice-type medians (from the 2026 Data Appendix).
// Used for the "what drives the price" visual on the hub. Selectable text only,
// never rendered as an image.
// ---------------------------------------------------------------------------
export interface PracticeTypeRow {
  label: string;
  median: number;
}
export const PRACTICE_TYPE_MEDIANS: PracticeTypeRow[] = [
  { label: "Mental / behavioral health", median: 549 },
  { label: "Primary care", median: 499 },
  { label: "Medical weight loss", median: 499 },
  { label: "Aesthetics / med spa", median: 599 },
];

// ---------------------------------------------------------------------------
// Target query sets (spec §4.5) — for FAQ selection & heading phrasing only.
// ---------------------------------------------------------------------------
export const HUB_TARGET_QUERIES = [
  "how much does a collaborating physician cost",
  "collaborating physician cost",
  "nurse practitioner collaborating physician cost per month",
  "average cost of a collaborating physician",
  "how much do NPs pay for physician collaboration",
  "collaborating physician fees 2026",
  "what is a fair price for a collaborating physician",
  "nurse practitioner supervision cost",
];

// ---------------------------------------------------------------------------
// Palette — aligned to the official Fair Price Report / brand system:
//   ink navy #0B1220 · royal blue #4052FF · teal #2FCF9E · amber #F5A623.
// Royal blue is the primary accent (answers, links, CTAs); navy ink is the
// structural color (table headers); teal marks Fair Price benchmark data;
// amber flags the study edition. Kept semantically distinct from live listings.
// (Token scale for these lives in tailwind.config.ts under `fp`.)
// ---------------------------------------------------------------------------
export const PALETTE = {
  calloutBg: "#EEF0FF", // royal-blue tint — answer callout + highlighted NPC row
  calloutBorder: "#4052FF", // royal blue — answer callout accent + chart bars
  tableHeaderBg: "#0B1220", // ink navy — table header rows (matches report)
  tableRowBorder: "#e5e7eb",
} as const;

// ---------------------------------------------------------------------------
// Paths & report asset
// ---------------------------------------------------------------------------
export const HUB_PATH = "/collaborating-physician-cost/";
export const METHODOLOGY_PATH = "/collaborating-physician-cost/methodology/";
export const DATA_JSON_PATH = "/collaborating-physician-cost/data.json";
export const REPORT_PDF_PATH = "/collaborating-physician-cost/fair-price-report-2026.pdf";
export const DOWNLOAD_REPORT_PATH = "/download-report";

export function spokePath(slug: string): string {
  return `/collaborating-physician-cost/${slug}/`;
}
export function stateGuidePath(slug: string): string {
  return `/collaborating-physician/${slug}/`;
}
